import { ColDef } from '@ag-grid-community/core';

import { rawAttributeColumnRenderer } from '@shared/components/ag-raw-attribute-renderer/ag-raw-attribute-renderer';
import { GlobalState } from '@shared/store/global/global.model';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

interface GenericGetAttributeColumnsParams<T> {
  attributeConfig: Pick<GlobalState, 'attributes' | 'primaryAttribute' | 'secondaryAttribute'> & {
    gridData: T[];
  };
  colDefOverrides?: Partial<ColDef>;
  offset?: number;
  // true will return only primary and secondary attributes
  // false will return all attributes
  // null will return all attributes without primary and secondary
  onlyPivot: boolean | null;
  showHeaderTooltip?: boolean;
  widthConfig?: {
    maxWidth?: number;
    minWidth?: number;
    offset?: number;
  };
}

export const genericGetAttributeColumns = <T>({
  attributeConfig,
  colDefOverrides = {},
  offset = 0,
  onlyPivot = false,
  showHeaderTooltip = false,
  widthConfig,
}: GenericGetAttributeColumnsParams<T>) => {
  const { attributes, primaryAttribute, secondaryAttribute } = attributeConfig;

  const getAttributeColumn = ({
    headerName,
    index,
    name,
  }: {
    headerName: string;
    index: number;
    name: string;
  }) => {
    const field = getEncodedAttributeName(name);

    return <ColDef>{
      field,
      headerName,
      headerTooltip: showHeaderTooltip ? headerName : undefined,
      tooltipField: field,
      ...rawAttributeColumnRenderer(attributeConfig, name, index, widthConfig),
      ...colDefOverrides,
    };
  };

  const pivotColumns = [primaryAttribute, secondaryAttribute]
    .map((attr) => attr.attribute_name)
    .filter(Boolean)
    .map((attr, index) => {
      return getAttributeColumn({
        headerName: attr + ' *',
        index: index + offset,
        name: attr,
      });
    });

  const otherColumns = attributes
    .filter(
      (attr) =>
        attr.attribute_name !== primaryAttribute.attribute_name &&
        attr.attribute_name !== secondaryAttribute.attribute_name
    )
    .sort((a, b) => Utils.localeAlphaNumSort(a.attribute_name || '', b.attribute_name || ''))
    .map((attr, index) => {
      return getAttributeColumn({
        headerName: attr.attribute_name,
        index: index + offset + pivotColumns.length,
        name: attr.attribute_name,
      });
    });

  let attributeColumns: ColDef[] = [];

  if (onlyPivot) attributeColumns = pivotColumns;

  if (onlyPivot === false) attributeColumns = [...pivotColumns, ...otherColumns];

  if (onlyPivot === null) attributeColumns = otherColumns;

  return attributeColumns;
};
